'use client';

import type {
  MysteryRoomImpactEffectState,
  MysteryRoomIncidentState,
  MysteryRoomInventoryItemState,
  MysteryRoomLocalBotState,
  MysteryRoomLocalRuntime,
  MysteryRoomNpcTaskState,
  MysteryRoomProjectileState,
  MysteryRoomHeroState,
} from '../core/localRuntime';
import type { P2pHostShootAction } from '../net/p2pTransport';
import { rectToSegments, polygonToSegments, type MysteryRoomCollisionShape, type MysteryRoomSegment, type MysteryRoomVec2 } from '../world/mapTypes';
import { facingFromVelocity } from './movementSystem';
import { npcHasSecurityBehavior, npcUsesZombieBehavior, resolveNpcFaction } from './npcBehaviorTreeSystem';

const PISTOL_RANGE = 980;
const PISTOL_COOLDOWN_MS = 180;
const PROJECTILE_TTL_MS = 140;
const IMPACT_TTL_MS = 520;
const BLOOD_POOL_TTL_MS = 60_000;
const MAX_PROJECTILES = 24;
const MAX_IMPACT_EFFECTS = 48;
const MEDKIT_HEAL_AMOUNT = 35;
const PISTOL_PROJECTILE_SPAWN_OFFSET_Y = -25;

type BulletHitKind = MysteryRoomProjectileState['hitKind'];

type BulletHit = {
  kind: BulletHitKind;
  id?: string;
  point: MysteryRoomVec2;
  distance: number;
};

export type MysteryRoomPlayerBulletTarget = { id: string; position: MysteryRoomVec2; radius: number };

export type MysteryRoomPistolFireOptions = {
  damageOverride?: number;
  /**
   * P2P-хост: id хоста в peerId, стрелок, тени гостей и кулдауны; при выстреле учитываются другие игроки.
   * shooterPeerId должен совпадать с runtime-логикой (хост стреляет — id хоста).
   */
  p2p?: MysteryRoomP2pPistolContext;
  /** P2P-гость: только трассер и откат, без локального урона */
  p2pGuestVisualOnly?: boolean;
};

/** Контекст для проверки попаданий в игроков на стороне хоста */
export type MysteryRoomP2pPistolContext = {
  hostPeerId: string;
  shooterPeerId: string;
  guestShadows: { current: Map<string, MysteryRoomHeroState> };
  lastShotMsByPeer: Map<string, number>;
};

/** Refs хоста для очереди выстрелов (без импорта React). */
export type MysteryRoomP2pHostPistolBundle = {
  hostPeerId: string;
  guestShadowHeroes: { current: Map<string, MysteryRoomHeroState> };
  lastShotMsByPeer: Map<string, number>;
};

export function getActiveMysteryRoomInventoryItem(runtime: MysteryRoomLocalRuntime): MysteryRoomInventoryItemState | null {
  if (!runtime.activeInventoryItemId) {
    return null;
  }
  return runtime.inventory.find((item) => item.id === runtime.activeInventoryItemId && (item.quantity ?? 1) > 0) ?? null;
}

export function isMysteryRoomPistolItem(item: MysteryRoomInventoryItemState | null | undefined): boolean {
  if (!item) {
    return false;
  }
  const label = item.label.toLowerCase();
  return item.id === 'pistol' || item.sourceObjectId?.startsWith('pistol') || label.includes('пистолет');
}

export function isMysteryRoomFlashlightItem(item: MysteryRoomInventoryItemState | null | undefined): boolean {
  if (!item) {
    return false;
  }
  return item.id === 'flashlight' || item.label.toLowerCase().includes('фонарик');
}

export function isMysteryRoomMedkitItem(item: MysteryRoomInventoryItemState | null | undefined): boolean {
  if (!item) {
    return false;
  }
  const label = item.label.toLowerCase();
  return item.id === 'medkit' || label.includes('аптечк') || label.includes('medkit') || label.includes('first aid');
}

export function isMysteryRoomPistolActive(runtime: MysteryRoomLocalRuntime): boolean {
  return isMysteryRoomPistolItem(getActiveMysteryRoomInventoryItem(runtime));
}

export function getMysteryRoomDefaultWeaponItem(runtime: MysteryRoomLocalRuntime): MysteryRoomInventoryItemState | null {
  const weapons = sortedAvailableInventory(runtime).filter((item) => item.isWeapon);
  return weapons.find(isMysteryRoomPistolItem) ?? weapons[0] ?? null;
}

export function getMysteryRoomFlashlightItem(runtime: MysteryRoomLocalRuntime): MysteryRoomInventoryItemState | null {
  return sortedAvailableInventory(runtime).find(isMysteryRoomFlashlightItem) ?? null;
}

export function getMysteryRoomActionBarItems(runtime: MysteryRoomLocalRuntime, limit = 5): MysteryRoomInventoryItemState[] {
  const defaultWeapon = getMysteryRoomDefaultWeaponItem(runtime);
  const flashlight = getMysteryRoomFlashlightItem(runtime);
  const reservedIds = new Set([defaultWeapon?.id, flashlight?.id].filter(Boolean));

  return sortedAvailableInventory(runtime)
    .filter((item) => item.kind !== 'access_card' && item.kind !== 'resource' && !reservedIds.has(item.id))
    .slice(0, limit);
}

export function setMysteryRoomActiveInventoryItem(
  runtime: MysteryRoomLocalRuntime,
  itemId: MysteryRoomInventoryItemState['id'] | null,
): MysteryRoomLocalRuntime {
  if (!itemId) {
    return runtime.activeInventoryItemId
      ? {
          ...runtime,
          activeInventoryItemId: null,
          events: ['Оружие убрано', ...runtime.events].slice(0, 10),
        }
      : runtime;
  }

  const item = runtime.inventory.find((entry) => entry.id === itemId && (entry.quantity ?? 1) > 0);
  if (!item) {
    return runtime;
  }
  if (item.kind === 'resource') {
    return appendCombatEvent(runtime, `${item.label}: ресурс строительства`);
  }

  return {
    ...runtime,
    activeInventoryItemId: item.id,
    events: [`Активен предмет: ${item.label}`, ...runtime.events].slice(0, 10),
  };
}

export function toggleMysteryRoomActiveInventoryItem(
  runtime: MysteryRoomLocalRuntime,
  itemId: MysteryRoomInventoryItemState['id'],
): MysteryRoomLocalRuntime {
  return setMysteryRoomActiveInventoryItem(runtime, runtime.activeInventoryItemId === itemId ? null : itemId);
}

export function toggleMysteryRoomDefaultWeapon(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  const weapon = getMysteryRoomDefaultWeaponItem(runtime);
  if (!weapon) {
    return appendCombatEvent(runtime, 'Оружия нет в инвентаре');
  }

  if (runtime.activeInventoryItemId === weapon.id) {
    return {
      ...runtime,
      activeInventoryItemId: null,
      events: [`${weapon.label}: убрано`, ...runtime.events].slice(0, 10),
    };
  }

  return {
    ...runtime,
    activeInventoryItemId: weapon.id,
    events: [`${weapon.label}: в руках`, ...runtime.events].slice(0, 10),
  };
}

export function toggleMysteryRoomFlashlight(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  if (!getMysteryRoomFlashlightItem(runtime)) {
    return appendCombatEvent(runtime, 'Фонарика нет в инвентаре');
  }

  const flashlightEnabled = !runtime.flashlightEnabled;
  return {
    ...runtime,
    flashlightEnabled,
    events: [`Фонарик ${flashlightEnabled ? 'включён' : 'выключен'}`, ...runtime.events].slice(0, 10),
  };
}

export function useMysteryRoomActionBarItem(
  runtime: MysteryRoomLocalRuntime,
  itemId: MysteryRoomInventoryItemState['id'],
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  const item = runtime.inventory.find((entry) => entry.id === itemId && (entry.quantity ?? 1) > 0);
  if (!item) {
    return runtime;
  }

  if (isMysteryRoomFlashlightItem(item)) {
    return toggleMysteryRoomFlashlight(runtime);
  }

  if (isMysteryRoomMedkitItem(item)) {
    return useMysteryRoomMedkit(runtime, item);
  }

  if (item.kind === 'access_card' || item.kind === 'resource') {
    return appendCombatEvent(runtime, `${item.label}: пассивный слот`);
  }

  return toggleMysteryRoomActiveInventoryItem(runtime, item.id);
}

export function fireMysteryRoomPistol(
  runtime: MysteryRoomLocalRuntime,
  target: MysteryRoomVec2,
  options: MysteryRoomPistolFireOptions = {},
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  if (options.p2pGuestVisualOnly) {
    return fireMysteryRoomPistolGuestVisual(runtime, target);
  }

  const activeItem = getActiveMysteryRoomInventoryItem(runtime);
  if (!isMysteryRoomPistolItem(activeItem)) {
    return appendCombatEvent(runtime, 'Пистолет не активен');
  }

  const ctx = options.p2p;
  if (ctx) {
    const lastPeer = ctx.lastShotMsByPeer.get(ctx.shooterPeerId) ?? -999;
    if (runtime.elapsedMs - lastPeer < PISTOL_COOLDOWN_MS) {
      return runtime;
    }
    ctx.lastShotMsByPeer.set(ctx.shooterPeerId, runtime.elapsedMs);
  } else if (runtime.elapsedMs - runtime.lastShotAtMs < PISTOL_COOLDOWN_MS) {
    return runtime;
  }

  const from = heroProjectileSpawnPoint(runtime);
  const playerTargets = ctx ? buildP2pPlayerBulletTargets(runtime, ctx, ctx.shooterPeerId) : undefined;
  return finalizePistolStrike(runtime, from, target, {
    ...options,
    applyCooldownToRuntime: true,
    playerTargets,
    p2pCtx: ctx,
  });
}

/** P2P-гость: трассер и откат без урона (авторитет — хост). */
export function fireMysteryRoomPistolGuestVisual(runtime: MysteryRoomLocalRuntime, target: MysteryRoomVec2): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }
  const activeItem = getActiveMysteryRoomInventoryItem(runtime);
  if (!isMysteryRoomPistolItem(activeItem)) {
    return appendCombatEvent(runtime, 'Пистолет не активен');
  }
  if (runtime.elapsedMs - runtime.lastShotAtMs < PISTOL_COOLDOWN_MS) {
    return runtime;
  }
  const from = heroProjectileSpawnPoint(runtime);
  return finalizePistolStrike(runtime, from, target, {
    applyCooldownToRuntime: true,
    skipDamage: true,
  });
}

/** Очередь выстрелов гостя на хосте — без проверки инвентаря у тени. */
export function processP2pHostShoot(
  runtime: MysteryRoomLocalRuntime,
  shot: P2pHostShootAction,
  ctx: MysteryRoomP2pPistolContext,
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running') {
    return runtime;
  }
  const lastPeer = ctx.lastShotMsByPeer.get(shot.fromPeerId) ?? -999;
  if (runtime.elapsedMs - lastPeer < PISTOL_COOLDOWN_MS) {
    return runtime;
  }
  ctx.lastShotMsByPeer.set(shot.fromPeerId, runtime.elapsedMs);

  const shooter =
    shot.fromPeerId === ctx.hostPeerId ? runtime.hero : ctx.guestShadows.current.get(shot.fromPeerId);
  if (!shooter?.alive) {
    return runtime;
  }

  const from = heroProjectileSpawnPointFromHero(shooter);
  const target = { x: shot.targetX, y: shot.targetY };
  const working = finalizePistolStrike(runtime, from, target, {
    applyCooldownToRuntime: shot.fromPeerId === ctx.hostPeerId,
    playerTargets: buildP2pPlayerBulletTargets(runtime, ctx, shot.fromPeerId),
    p2pCtx: ctx,
    shooterFacingPeerId: shot.fromPeerId,
    hostPeerIdForFacing: ctx.hostPeerId,
  });

  return working;
}

type FinalizePistolStrikeOptions = MysteryRoomPistolFireOptions & {
  applyCooldownToRuntime: boolean;
  playerTargets?: MysteryRoomPlayerBulletTarget[];
  skipDamage?: boolean;
  p2pCtx?: MysteryRoomP2pPistolContext;
  shooterFacingPeerId?: string;
  hostPeerIdForFacing?: string;
};

function buildP2pPlayerBulletTargets(
  runtime: MysteryRoomLocalRuntime,
  ctx: MysteryRoomP2pPistolContext,
  excludePeerId: string,
): MysteryRoomPlayerBulletTarget[] {
  const r = PLAYER_HIT_RADIUS;
  const out: MysteryRoomPlayerBulletTarget[] = [];
  if (ctx.hostPeerId !== excludePeerId && runtime.hero.alive) {
    out.push({ id: ctx.hostPeerId, position: runtime.hero.position, radius: r });
  }
  ctx.guestShadows.current.forEach((hero, peerId) => {
    if (peerId === excludePeerId || !hero.alive) {
      return;
    }
    out.push({ id: peerId, position: hero.position, radius: r });
  });
  return out;
}

const PLAYER_HIT_RADIUS = 22;

function finalizePistolStrike(
  runtime: MysteryRoomLocalRuntime,
  from: MysteryRoomVec2,
  target: MysteryRoomVec2,
  options: FinalizePistolStrikeOptions,
): MysteryRoomLocalRuntime {
  const vector = { x: target.x - from.x, y: target.y - from.y };
  const length = Math.hypot(vector.x, vector.y);
  if (length < 0.001) {
    return runtime;
  }
  const direction = { x: vector.x / length, y: vector.y / length };
  const to = { x: from.x + direction.x * PISTOL_RANGE, y: from.y + direction.y * PISTOL_RANGE };
  const hit = options.skipDamage ? resolveBulletHit(runtime, from, to) : resolveBulletHit(runtime, from, to, options.playerTargets);

  let nextHero = runtime.hero;
  if (options.shooterFacingPeerId && options.hostPeerIdForFacing) {
    if (options.shooterFacingPeerId === options.hostPeerIdForFacing) {
      nextHero = {
        ...runtime.hero,
        facing: facingFromVelocity(direction, runtime.hero.facing),
        hidden: false,
        hideZoneId: null,
      };
    } else if (options.p2pCtx) {
      const sh = options.p2pCtx.guestShadows.current.get(options.shooterFacingPeerId);
      if (sh) {
        options.p2pCtx.guestShadows.current.set(options.shooterFacingPeerId, {
          ...sh,
          facing: facingFromVelocity(direction, sh.facing),
          hidden: false,
          hideZoneId: null,
        });
      }
    }
  } else {
    nextHero = {
      ...runtime.hero,
      facing: facingFromVelocity(direction, runtime.hero.facing),
      hidden: false,
      hideZoneId: null,
    };
  }

  const impactPoint = hit?.point ?? to;
  const projectile: MysteryRoomProjectileState = {
    id: `bullet_${runtime.elapsedMs}_${runtime.projectiles.length}`,
    kind: 'bullet',
    from,
    to: impactPoint,
    createdAtMs: runtime.elapsedMs,
    ttlMs: PROJECTILE_TTL_MS,
    hitKind: hit?.kind ?? 'none',
    hitId: hit?.id,
  };

  let baseRuntime: MysteryRoomLocalRuntime = {
    ...runtime,
    hero: nextHero,
    input: { ...runtime.input, pointerTarget: null },
    lastShotAtMs: options.applyCooldownToRuntime ? runtime.elapsedMs : runtime.lastShotAtMs,
    projectiles: [...runtime.projectiles, projectile].slice(-MAX_PROJECTILES),
  };

  if (options.skipDamage || !hit) {
    return appendCombatEvent(baseRuntime, options.skipDamage ? 'Выстрел…' : 'Выстрел: мимо');
  }

  if (hit.kind === 'npc' && hit.id) {
    return applyPistolDamageToNpc(baseRuntime, hit.id, impactPoint, options.damageOverride);
  }

  if (hit.kind === 'bot' && hit.id) {
    return applyPistolDamageToLocalBot(baseRuntime, hit.id, impactPoint);
  }

  if (hit.kind === 'player' && hit.id && options.p2pCtx) {
    return applyPistolDamageToPlayer(baseRuntime, hit.id, impactPoint, options.p2pCtx, options.damageOverride);
  }

  return {
    ...appendCombatEvent(baseRuntime, hit.kind === 'none' ? 'Выстрел: мимо' : 'Пуля попала в препятствие'),
    impactEffects: appendImpactEffect(baseRuntime.impactEffects, {
      id: `spark_${baseRuntime.elapsedMs}_${baseRuntime.impactEffects.length}`,
      kind: 'spark',
      position: impactPoint,
      radius: 34,
      createdAtMs: baseRuntime.elapsedMs,
      ttlMs: IMPACT_TTL_MS,
      sourceId: hit.id,
    }),
  };
}

function applyPistolDamageToPlayer(
  runtime: MysteryRoomLocalRuntime,
  victimPeerId: string,
  hitPoint: MysteryRoomVec2,
  ctx: MysteryRoomP2pPistolContext,
  damageOverride?: number,
): MysteryRoomLocalRuntime {
  const damage =
    typeof damageOverride === 'number' && Number.isFinite(damageOverride)
      ? Math.max(0, Math.round(damageOverride))
      : 5 + Math.floor(Math.random() * 11);

  const bloodSplash: MysteryRoomImpactEffectState = {
    id: `blood_splash_${runtime.elapsedMs}_${runtime.impactEffects.length}`,
    kind: 'blood_splash',
    position: hitPoint,
    radius: 38,
    createdAtMs: runtime.elapsedMs,
    ttlMs: IMPACT_TTL_MS,
    sourceId: victimPeerId,
  };

  if (victimPeerId === ctx.hostPeerId) {
    const killed = runtime.hero.health - damage <= 0;
    const nextHealth = Math.max(0, runtime.hero.health - damage);
    const bloodPool: MysteryRoomImpactEffectState | null = killed
      ? {
          id: `blood_pool_${runtime.elapsedMs}_${runtime.impactEffects.length + 1}`,
          kind: 'blood_pool',
          position: { ...runtime.hero.position },
          radius: 46,
          createdAtMs: runtime.elapsedMs,
          ttlMs: BLOOD_POOL_TTL_MS,
          sourceId: victimPeerId,
        }
      : null;
    return {
      ...runtime,
      hero: {
        ...runtime.hero,
        health: nextHealth,
        alive: !killed,
        velocity: killed ? { x: 0, y: 0 } : runtime.hero.velocity,
        movementMode: killed ? 'idle' : runtime.hero.movementMode,
        animationState: killed ? 'idle' : runtime.hero.animationState,
      },
      impactEffects: appendImpactEffect(runtime.impactEffects, bloodSplash, bloodPool ?? undefined),
      events: [
        killed ? 'Пистолет: попадание — вы убиты' : `Пистолет: в вас −${damage} HP`,
        ...runtime.events,
      ].slice(0, 10),
    };
  }

  const shadow = ctx.guestShadows.current.get(victimPeerId);
  if (!shadow?.alive) {
    return runtime;
  }
  const killed = shadow.health - damage <= 0;
  const nextHealth = Math.max(0, shadow.health - damage);
  ctx.guestShadows.current.set(victimPeerId, {
    ...shadow,
    health: nextHealth,
    alive: !killed,
    velocity: killed ? { x: 0, y: 0 } : shadow.velocity,
    movementMode: killed ? 'idle' : shadow.movementMode,
    animationState: killed ? 'idle' : shadow.animationState,
  });
  const bloodPool: MysteryRoomImpactEffectState | null = killed
    ? {
        id: `blood_pool_${runtime.elapsedMs}_${runtime.impactEffects.length + 1}`,
        kind: 'blood_pool',
        position: { ...shadow.position },
        radius: 46,
        createdAtMs: runtime.elapsedMs,
        ttlMs: BLOOD_POOL_TTL_MS,
        sourceId: victimPeerId,
      }
    : null;
  return {
    ...runtime,
    impactEffects: appendImpactEffect(runtime.impactEffects, bloodSplash, bloodPool ?? undefined),
    events: [
      killed ? `Пистолет: гость нейтрализован` : `Пистолет: попадание в гостя −${damage} HP`,
      ...runtime.events,
    ].slice(0, 10),
  };
}

function closestPlayerHit(from: MysteryRoomVec2, to: MysteryRoomVec2, targets: MysteryRoomPlayerBulletTarget[]): BulletHit | null {
  const hits: BulletHit[] = [];
  for (const t of targets) {
    const hit = circleHit(from, to, t.position, Math.max(18, t.radius));
    if (hit) {
      hits.push({ ...hit, kind: 'player', id: t.id });
    }
  }
  return closestHit(hits);
}

export function heroProjectileSpawnPoint(runtime: Pick<MysteryRoomLocalRuntime, 'hero'>): MysteryRoomVec2 {
  return heroProjectileSpawnPointFromHero(runtime.hero);
}

function heroProjectileSpawnPointFromHero(hero: Pick<MysteryRoomHeroState, 'position'>): MysteryRoomVec2 {
  return {
    x: hero.position.x,
    y: hero.position.y + PISTOL_PROJECTILE_SPAWN_OFFSET_Y,
  };
}

export function updateMysteryRoomCombat(runtime: MysteryRoomLocalRuntime, _dtMs: number): MysteryRoomLocalRuntime {
  const activeItem = getActiveMysteryRoomInventoryItem(runtime);
  const flashlight = getMysteryRoomFlashlightItem(runtime);
  return {
    ...runtime,
    activeInventoryItemId: activeItem ? runtime.activeInventoryItemId : null,
    flashlightEnabled: flashlight ? runtime.flashlightEnabled : false,
    projectiles: runtime.projectiles.filter((projectile) => runtime.elapsedMs - projectile.createdAtMs <= projectile.ttlMs),
    impactEffects: runtime.impactEffects.filter((effect) => runtime.elapsedMs - effect.createdAtMs <= effect.ttlMs),
  };
}

function sortedAvailableInventory(runtime: MysteryRoomLocalRuntime): MysteryRoomInventoryItemState[] {
  return [...runtime.inventory]
    .filter((item) => (item.quantity ?? 1) > 0)
    .sort((a, b) => a.slotIndex - b.slotIndex);
}

function useMysteryRoomMedkit(
  runtime: MysteryRoomLocalRuntime,
  item: MysteryRoomInventoryItemState,
): MysteryRoomLocalRuntime {
  if (runtime.hero.health >= runtime.hero.maxHealth) {
    return appendCombatEvent(runtime, `${item.label}: здоровье уже полное`);
  }

  const nextHealth = Math.min(runtime.hero.maxHealth, runtime.hero.health + MEDKIT_HEAL_AMOUNT);
  const healed = nextHealth - runtime.hero.health;
  const nextInventory = runtime.inventory
    .map((entry) => (entry.id === item.id ? { ...entry, quantity: Math.max(0, entry.quantity - 1) } : entry))
    .filter((entry) => entry.quantity > 0)
    .map((entry, slotIndex) => ({ ...entry, slotIndex }));
  const itemStillExists = nextInventory.some((entry) => entry.id === item.id);

  return {
    ...runtime,
    hero: {
      ...runtime.hero,
      health: nextHealth,
    },
    inventory: nextInventory,
    activeInventoryItemId: runtime.activeInventoryItemId === item.id && !itemStillExists ? null : runtime.activeInventoryItemId,
    events: [`${item.label}: +${healed} HP`, ...runtime.events].slice(0, 10),
  };
}

function applyPistolDamageToNpc(
  runtime: MysteryRoomLocalRuntime,
  npcId: string,
  hitPoint: MysteryRoomVec2,
  damageOverride?: number,
): MysteryRoomLocalRuntime {
  const damage = typeof damageOverride === 'number' && Number.isFinite(damageOverride)
    ? Math.max(0, Math.round(damageOverride))
    : 5 + Math.floor(Math.random() * 11);
  const target = runtime.npcs.find((npc) => npc.id === npcId);
  if (!target || !target.alive) {
    return runtime;
  }
  const nextHealth = Math.max(0, target.health - damage);
  const killed = nextHealth <= 0;
  const woundedEvacuationTask: MysteryRoomNpcTaskState = {
    kind: 'evacuate',
    score: 0.82,
    enterThreshold: 0.45,
    exitThreshold: 0.2,
    takeoverMargin: 0.12,
    minHoldMs: 3_000,
    assignedIncidentId: null,
    targetNodeId: target.evacuationNodeId,
    targetPoint: null,
    targetZoneId: null,
    anchorId: null,
    anchorPoint: null,
  };
  const nextNpcs = runtime.npcs.map((npc) => {
    if (npc.id !== npcId) {
      return npc;
    }
    const usesZombieBehavior = npcUsesZombieBehavior(npc);
    const countersAttack = !usesZombieBehavior && (npcHasSecurityBehavior(npc) || resolveNpcFaction(npc) === 'hostile');
    const woundedEvacuation = !usesZombieBehavior && !countersAttack ? woundedEvacuationTask : npc.currentTask;

    return {
      ...npc,
      health: nextHealth,
      alive: !killed,
      killedAtMs: killed ? runtime.elapsedMs : npc.killedAtMs,
      velocity: killed ? { x: 0, y: 0 } : npc.velocity,
      animationState: killed ? ('idle' as const) : npc.animationState,
      awarenessState: killed ? ('patrol' as const) : countersAttack ? ('alert' as const) : npc.awarenessState,
      seesHero: killed ? false : npc.seesHero,
      suspicionMs: killed ? 0 : npc.suspicionMs,
      investigateTarget: killed ? null : countersAttack ? { ...runtime.hero.position } : npc.investigateTarget,
      searchRemainingMs: killed ? 0 : countersAttack ? 1_400 : npc.searchRemainingMs,
      currentTask: killed ? null : woundedEvacuation,
      navPath: killed || !countersAttack ? [] : npc.navPath,
      navIndex: killed || !countersAttack ? 0 : npc.navIndex,
      currentTargetNodeId: killed ? null : countersAttack ? npc.currentTargetNodeId : null,
      currentRoleAction: killed ? null : npc.currentRoleAction,
      roleActionRemainingMs: killed ? 0 : npc.roleActionRemainingMs,
    };
  });
  const nextIncidents = killed ? runtime.incidents : upsertNpcInjuryIncident(runtime, target, nextHealth);
  const bloodSplash: MysteryRoomImpactEffectState = {
    id: `blood_splash_${runtime.elapsedMs}_${runtime.impactEffects.length}`,
    kind: 'blood_splash',
    position: hitPoint,
    radius: 38,
    createdAtMs: runtime.elapsedMs,
    ttlMs: IMPACT_TTL_MS,
    sourceId: npcId,
  };
  const bloodPool: MysteryRoomImpactEffectState | null = killed
    ? {
        id: `blood_pool_${runtime.elapsedMs}_${runtime.impactEffects.length + 1}`,
        kind: 'blood_pool',
        position: { ...target.position },
        radius: 46,
        createdAtMs: runtime.elapsedMs,
        ttlMs: BLOOD_POOL_TTL_MS,
        sourceId: npcId,
      }
    : null;

  return {
    ...runtime,
    npcs: nextNpcs,
    incidents: nextIncidents,
    hero: {
      ...runtime.hero,
      spottedByNpcIds: killed ? runtime.hero.spottedByNpcIds.filter((id) => id !== npcId) : runtime.hero.spottedByNpcIds,
      alertState: killed && runtime.hero.spottedByNpcIds.length <= 1 ? 'clear' : runtime.hero.alertState,
    },
    impactEffects: appendImpactEffect(runtime.impactEffects, bloodSplash, bloodPool ?? undefined),
    events: [
      killed ? `Пистолет: ${target.name} нейтрализован` : `Пистолет: ${target.name} -${damage} HP`,
      ...runtime.events,
    ].slice(0, 10),
  };
}

function applyPistolDamageToLocalBot(
  runtime: MysteryRoomLocalRuntime,
  botId: string,
  hitPoint: MysteryRoomVec2,
): MysteryRoomLocalRuntime {
  const bot = runtime.localBots.find((entry) => entry.id === botId);
  if (!bot) {
    return runtime;
  }

  const bloodSplash: MysteryRoomImpactEffectState = {
    id: `blood_splash_${runtime.elapsedMs}_${runtime.impactEffects.length}`,
    kind: 'blood_splash',
    position: hitPoint,
    radius: 32,
    createdAtMs: runtime.elapsedMs,
    ttlMs: IMPACT_TTL_MS,
    sourceId: botId,
  };
  const bloodPool: MysteryRoomImpactEffectState = {
    id: `blood_pool_${runtime.elapsedMs}_${runtime.impactEffects.length + 1}`,
    kind: 'blood_pool',
    position: { ...bot.position },
    radius: 42,
    createdAtMs: runtime.elapsedMs,
    ttlMs: BLOOD_POOL_TTL_MS,
    sourceId: botId,
  };

  return {
    ...runtime,
    localBots: runtime.localBots.filter((entry) => entry.id !== botId),
    impactEffects: appendImpactEffect(runtime.impactEffects, bloodSplash, bloodPool),
    events: [`Пистолет: бот ${bot.name} устранён`, ...runtime.events].slice(0, 10),
  };
}

function upsertNpcInjuryIncident(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomLocalRuntime['npcs'][number],
  health: number,
): MysteryRoomIncidentState[] {
  if (health >= npc.maxHealth) {
    return runtime.incidents;
  }

  const id = `injured_person_${npc.id}`;
  const severity = Math.max(0.45, Math.min(0.95, 1 - health / Math.max(1, npc.maxHealth)));
  const existing = runtime.incidents.find((incident) => incident.id === id);
  const nextIncident: MysteryRoomIncidentState =
    existing && existing.status !== 'resolved'
      ? {
          ...existing,
          kind: 'injured_person',
          zoneId: npc.zoneId,
          position: { ...npc.position },
          severity,
          sourceType: 'player',
          sourceActorId: npc.id,
        }
      : {
          id,
          kind: 'injured_person',
          zoneId: npc.zoneId,
          position: { ...npc.position },
          sourceType: 'player',
          sourceActorId: npc.id,
          severity,
          status: 'active',
          assignedNpcId: null,
          leaseUntilMs: 0,
          createdAtMs: runtime.elapsedMs,
          resolvedAtMs: null,
          resolutionAction: null,
          resolutionRemainingMs: 0,
        };

  return [...runtime.incidents.filter((incident) => incident.id !== id), nextIncident];
}

function resolveBulletHit(
  runtime: MysteryRoomLocalRuntime,
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
  playerTargets?: MysteryRoomPlayerBulletTarget[],
): BulletHit | null {
  const obstacleHit = closestObstacleHit(runtime, from, to);
  const npcHit = closestNpcHit(runtime, from, to);
  const localBotHit = closestLocalBotHit(runtime.localBots, from, to);
  const playerHit = playerTargets?.length ? closestPlayerHit(from, to, playerTargets) : null;
  return pickClosestHit(obstacleHit, npcHit, localBotHit, playerHit);
}

function pickClosestHit(...hits: Array<BulletHit | null | undefined>): BulletHit | null {
  const compact = hits.filter((h): h is BulletHit => Boolean(h));
  return compact.length ? compact.reduce((a, b) => (a.distance <= b.distance ? a : b)) : null;
}

function closestObstacleHit(runtime: MysteryRoomLocalRuntime, from: MysteryRoomVec2, to: MysteryRoomVec2): BulletHit | null {
  const hits: BulletHit[] = [];

  runtime.map.walls.forEach((wall, index) => {
    const hit = segmentHit(from, to, wall);
    if (hit) {
      hits.push({ ...hit, kind: 'wall', id: `wall:${index}` });
    }
  });

  runtime.doors
    .filter((door) => door.openProgress < 0.85)
    .forEach((door) => {
      const hit = closestShapeSegmentsHit(from, to, rectToSegments(door.panel));
      if (hit) {
        hits.push({ ...hit, kind: 'door', id: door.id });
      }
    });

  runtime.map.customObjects.forEach((object) => {
    const shape = object.collisionEnabled === false ? { kind: 'none' as const } : object.collision;
    if (!customObjectBlocksBullet(shape)) {
      return;
    }
    const hit = collisionShapeHit(from, to, shape);
    if (hit) {
      hits.push({ ...hit, kind: 'object', id: object.id });
    }
  });

  return closestHit(hits);
}

function closestNpcHit(runtime: MysteryRoomLocalRuntime, from: MysteryRoomVec2, to: MysteryRoomVec2): BulletHit | null {
  const hits: BulletHit[] = [];
  runtime.npcs
    .filter((npc) => npc.alive)
    .forEach((npc) => {
      const hit = circleHit(from, to, npc.position, Math.max(18, npc.radius));
      if (hit) {
        hits.push({ ...hit, kind: 'npc', id: npc.id });
      }
    });
  return closestHit(hits);
}

function closestLocalBotHit(localBots: MysteryRoomLocalBotState[], from: MysteryRoomVec2, to: MysteryRoomVec2): BulletHit | null {
  const hits: BulletHit[] = [];
  localBots.forEach((bot) => {
    const hit = circleHit(from, to, bot.position, Math.max(18, bot.radius));
    if (hit) {
      hits.push({ ...hit, kind: 'bot', id: bot.id });
    }
  });
  return closestHit(hits);
}

function customObjectBlocksBullet(shape: MysteryRoomCollisionShape): boolean {
  if (shape.kind === 'none') {
    return false;
  }
  return shape.blocksMovement || shape.blocksSight;
}

function collisionShapeHit(from: MysteryRoomVec2, to: MysteryRoomVec2, shape: MysteryRoomCollisionShape): Omit<BulletHit, 'kind' | 'id'> | null {
  if (shape.kind === 'rect') {
    return closestShapeSegmentsHit(from, to, rectToSegments(shape.rect));
  }
  if (shape.kind === 'polygon') {
    return closestShapeSegmentsHit(from, to, polygonToSegments(shape.points));
  }
  if (shape.kind === 'circle') {
    return circleHit(from, to, shape.center, shape.radius);
  }
  return null;
}

function closestShapeSegmentsHit(from: MysteryRoomVec2, to: MysteryRoomVec2, segments: MysteryRoomSegment[]): Omit<BulletHit, 'kind' | 'id'> | null {
  return closestHit(segments.map((segment) => segmentHit(from, to, segment)).filter((hit): hit is Omit<BulletHit, 'kind' | 'id'> => Boolean(hit)));
}

function segmentHit(from: MysteryRoomVec2, to: MysteryRoomVec2, segment: MysteryRoomSegment): Omit<BulletHit, 'kind' | 'id'> | null {
  const r = { x: to.x - from.x, y: to.y - from.y };
  const s = { x: segment.end.x - segment.start.x, y: segment.end.y - segment.start.y };
  const denominator = cross(r, s);
  if (Math.abs(denominator) < 0.00001) {
    return null;
  }
  const qp = { x: segment.start.x - from.x, y: segment.start.y - from.y };
  const t = cross(qp, s) / denominator;
  const u = cross(qp, r) / denominator;
  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null;
  }
  const length = Math.hypot(r.x, r.y);
  return {
    point: { x: from.x + r.x * t, y: from.y + r.y * t },
    distance: t * length,
  };
}

function circleHit(
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
  center: MysteryRoomVec2,
  radius: number,
): Omit<BulletHit, 'kind' | 'id'> | null {
  const d = { x: to.x - from.x, y: to.y - from.y };
  const f = { x: from.x - center.x, y: from.y - center.y };
  const a = d.x * d.x + d.y * d.y;
  const b = 2 * (f.x * d.x + f.y * d.y);
  const c = f.x * f.x + f.y * f.y - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0 || a <= 0) {
    return null;
  }
  const sqrt = Math.sqrt(discriminant);
  const t1 = (-b - sqrt) / (2 * a);
  const t2 = (-b + sqrt) / (2 * a);
  const t = [t1, t2].find((candidate) => candidate >= 0 && candidate <= 1);
  if (typeof t !== 'number') {
    return null;
  }
  const totalDistance = Math.hypot(d.x, d.y);
  return {
    point: { x: from.x + d.x * t, y: from.y + d.y * t },
    distance: totalDistance * t,
  };
}

function closestHit<T extends { distance: number }>(hits: T[]): T | null {
  if (hits.length === 0) {
    return null;
  }
  return hits.reduce((closest, hit) => (hit.distance < closest.distance ? hit : closest));
}

function cross(a: MysteryRoomVec2, b: MysteryRoomVec2) {
  return a.x * b.y - a.y * b.x;
}

function appendImpactEffect(
  current: MysteryRoomImpactEffectState[],
  effect: MysteryRoomImpactEffectState,
  secondEffect?: MysteryRoomImpactEffectState,
) {
  return [...current, effect, ...(secondEffect ? [secondEffect] : [])].slice(-MAX_IMPACT_EFFECTS);
}

function appendCombatEvent(runtime: MysteryRoomLocalRuntime, message: string): MysteryRoomLocalRuntime {
  return {
    ...runtime,
    events: [message, ...runtime.events].slice(0, 10),
  };
}
